import * as React from "react";
import Grid from "@mui/material/Grid";
import {debounce} from "@mui/material/utils";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function loadScript(src, position, id) {
	return new Promise((resolve, reject) => {
		if (!position) {
			reject(new Error("Invalid position to append script"));
			return;
		}

		const script = document.createElement("script");
		script.setAttribute("async", "");
		script.setAttribute("id", id);
		script.src = src;

		script.onload = () => resolve();
		script.onerror = () => reject(new Error(`Failed to load script: ${src}`));

		position.appendChild(script);
	});
}

const autocompleteService = {current: null};

const loginStyle = {
	"& .MuiOutlinedInput-root": {
		borderRadius: "8px",
		backgroundColor: "#fff",
		"& .MuiOutlinedInput-input": {
			padding: "10px 10px",
		},
		"& fieldset": {
			borderColor: "#e9ecef",
			borderWidth: "2px",
		},
		"&:hover fieldset": {
			borderColor: "#e9ecef",
			borderWidth: "2px",
		},
		"&.Mui-focused fieldset": {
			borderColor: "#22c55e",
			borderWidth: "2px",
			boxShadow: "0 0 0 .2rem #22c55e40",
		},
	},
	"& .MuiInputLabel-root": {
		color: "#e9ecef",
		borderWidth: "2px",
	},
	"& .MuiInputLabel-root.Mui-focused": {
		color: "#4caf50",
		borderWidth: "2px",
	},
};

const dashboardStyle = {
	"& .MuiOutlinedInput-root": {
		borderRadius: "8px",
		backgroundColor: "#fff",
		"& .MuiOutlinedInput-input": {
			padding: "5px 10px",
		},
		"& fieldset": {
			borderColor: "#e9ecef",
			borderWidth: "2px",
		},
		"&:hover fieldset": {
			borderColor: "#e9ecef",
			borderWidth: "2px",
		},
		"&.Mui-focused fieldset": {
			borderColor: "#22c55e",
			borderWidth: "2px",
			boxShadow: "0 0 0 .2rem #22c55e40",
		},
	},
	"& .MuiInputLabel-root": {
		color: "#e9ecef",
		borderWidth: "2px",
	},
	"& .MuiInputLabel-root.Mui-focused": {
		color: "#4caf50",
		borderWidth: "2px",
	},
};

const homeStyle = {
	"& .MuiOutlinedInput-root": {
		borderRadius: "50px",
		backgroundColor: "#fff",
		"& .MuiOutlinedInput-input": {padding: "10px 20px"},
		"& fieldset": {borderColor: "#e9ecef", borderWidth: "2px"},
		"&:hover fieldset": {borderColor: "#e9ecef", borderWidth: "2px"},
		"&.Mui-focused fieldset": {
			borderColor: "#22c55e",
			borderWidth: "2px",
			boxShadow: "0 0 0 .2rem #22c55e40",
		},
	},
	"& .MuiInputLabel-root": {color: "#e9ecef"},
	"& .MuiInputLabel-root.Mui-focused": {color: "#4caf50"},
};

export function GoogleAddress({address, callBackAction, styleFlag = "homeStyle"}) {
	const [value, setValue] = React.useState(address || null);
	const [inputValue, setInputValue] = React.useState("");
	const [options, setOptions] = React.useState([]);
	const loaded = React.useRef(false);

	const fetch = React.useMemo(
		() =>
			debounce((request, callback) => {
				autocompleteService.current.getPlacePredictions(request, callback);
			}, 400),
		[],
	);

	if (typeof window !== "undefined" && !loaded.current) {
		// Check if script already exists
		const existingScript = document.getElementById("google-maps");
		if (!existingScript) {
			loadScript(`https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&sensor=false&limit=5`, document.querySelector("head"), "google-maps")
				.then(() => {
					loaded.current = true;
					// Wait a bit for Google Maps to fully initialize
					setTimeout(() => {
						if (window.google?.maps?.places) {
							try {
								autocompleteService.current = new window.google.maps.places.AutocompleteService();
							} catch (err) {
								console.error("Failed to initialize AutocompleteService", err);
							}
						}
					}, 100);
				})
				.catch((error) => {
					console.error("Google Address Error", error);
					loaded.current = false;
				});
		} else {
			// Script already exists, just mark as loaded
			loaded.current = true;
		}
	}

	React.useEffect(() => {
		let active = true;
		let customResults = [];

		// Wait for Google Maps to be fully loaded
		const initAutocompleteService = () => {
			if (!autocompleteService.current && window.google) {
				try {
					if (window.google?.maps?.places?.AutocompleteService) {
						autocompleteService.current = new window.google.maps.places.AutocompleteService();
					}
				} catch (err) {
					console.error("Error initializing AutocompleteService", err);
				}
			}
		};

		// Try to initialize immediately
		initAutocompleteService();

		// If not ready, wait a bit and try again
		if (!autocompleteService.current && window.google) {
			const timeout = setTimeout(() => {
				initAutocompleteService();
			}, 200);
			return () => clearTimeout(timeout);
		}

		if (!autocompleteService.current) {
			return undefined;
		}

		if (inputValue === "" && address) {
			setOptions(value ? [value] : []);
			return undefined;
		}

		// Database search
		const searchDatabase = async () => {
			try {
				const res = await axios.get(`${API_URL}/api/custom-search-address`, {
					params: {query: inputValue},
				});
				if (res.data?.data?.length > 0) {
					customResults = res.data.data;
				}
			} catch (error) {
				console.error("Database search failed", error);
			}
		};

		searchDatabase();

		if (inputValue.length > 2 && autocompleteService.current) {
			try {
				fetch({input: inputValue, language: "en"}, (results) => {
					if (active) {
						let newOptions = value ? [value] : [];
						if (results) {
							newOptions = [...newOptions, ...results];
						}

						const tempArray = newOptions.map((item) => {
							if (customResults.length > 0) {
								const temp = customResults.find((i) => i.full_address === item.description);
								if (temp) {
									const index = customResults.findIndex((i) => i.full_address === item.description);
									if (index > -1) customResults.splice(index, 1);
									return {...temp, description: temp.full_address};
								}
							}
							return item;
						});

						setValue(inputValue);

						const find = options?.find((i) => i?.description === inputValue || i?.full_address === inputValue);

						if (find?.description) {
							callBackAction({type: "selected", value: inputValue});
						} else if (find?.full_address) {
							callBackAction({type: "selected", value: inputValue});
						} else {
							callBackAction({type: "typed", value: inputValue});
						}

						setOptions([...tempArray, ...customResults]);
					}
				});
			} catch (error) {
				console.error("Error fetching place predictions", error);
			}
		}

		// clear options if nothing typed
		if (inputValue.length < 1) {
			setOptions([]);
			return;
		}

		return () => {
			active = false;
		};
	}, [value, inputValue, fetch, address]);

	React.useEffect(() => {
		setValue(address);
		setInputValue(address);
	}, [address]);

	const handleOnChange = (event, newValue) => {
		setValue(newValue);

		const string = newValue?.description ? newValue?.description : newValue?.full_address || "";

		const find = options?.find((i) => i?.description === string || i.full_address === string);

		if (typeof newValue === "string") {
			callBackAction({type: "typed", value: newValue});
		} else if (newValue?.full_address) {
			callBackAction({type: "selected", value: newValue?.full_address});
		} else if (newValue?.description) {
			callBackAction({type: "selected", value: newValue?.description});
		} else if (find?.description) {
			callBackAction({type: "selected", value: newValue?.description});
		} else if (find?.full_address) {
			callBackAction({type: "selected", value: newValue?.full_address});
		} else {
			callBackAction({type: "typed", value: ""});
		}
	};

	return (
		<Autocomplete
			fullWidth
			id="google-map-demo"
			popupIcon={null}
			freeSolo
			getOptionLabel={(option) => (typeof option === "string" ? option : option?.description || option?.full_address || "")}
			filterOptions={(x) => x}
			options={options}
			autoComplete
			includeInputInList
			filterSelectedOptions
			value={value}
			clearIcon={false}
			onChange={handleOnChange}
			onInputChange={(event, newInputValue) => {
				setInputValue(newInputValue);
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					placeholder="Enter an address (e.g. 123 Main St, City, State)"
					fullWidth
					variant="outlined"
					sx={styleFlag === "homeStyle" ? homeStyle : styleFlag === "loginStyle" ? loginStyle : dashboardStyle}
				/>
			)}
			renderOption={(props, option) => (
				<li {...props}>
					<Grid container alignItems="center">
						<Grid item sx={{display: "flex", width: 44}}>
							{option?.addresses_id ? (
								<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24">
									<path
										fill="currentColor"
										d="m19.485 20.154l-6.262-6.262q-.75.639-1.725.989t-1.96.35q-2.402 0-4.066-1.663T3.808 9.503T5.47 5.436t4.064-1.667t4.068 1.664T15.268 9.5q0 1.042-.369 2.017t-.97 1.668l6.262 6.261zM9.539 14.23q1.99 0 3.36-1.37t1.37-3.361t-1.37-3.36t-3.36-1.37t-3.361 1.37t-1.37 3.36t1.37 3.36t3.36 1.37"
									/>
								</svg>
							) : (
								<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24">
									<path fill="currentColor" d="M12 11.5A2.5 2.5 0 0 1 9.5 9A2.5 2.5 0 0 1 12 6.5A2.5 2.5 0 0 1 14.5 9a2.5 2.5 0 0 1-2.5 2.5M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7" />
								</svg>
							)}
						</Grid>
						<Grid item sx={{width: "calc(100% - 44px)", wordWrap: "break-word"}}>
							{typeof option === "string" ? option : option?.description || option?.full_address || ""}
						</Grid>
					</Grid>
				</li>
			)}
			noOptionsText="No any locations!"
			slotProps={{
				paper: {
					sx: {
						mt: 1,
						borderRadius: "8px",
						boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
						"& .MuiAutocomplete-listbox": {
							padding: "4px 0",
							"& li": {
								padding: "8px 12px",
								borderBottom: "1px solid #f0f0f0",
								"&:last-child": {borderBottom: "none"},
								"&:hover": {backgroundColor: "#f5f5f5"},
							},
						},
					},
				},
			}}
		/>
	);
}
